import React, { useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { number, string } from 'prop-types';
import useStyles from 'hooks/useStyles';
import cx from 'classnames';

import RatingAction from 'components/RatingAction';
import BrightcovePlayer from 'components/BrightcovePlayer';
import RatingModal from 'components/RatingModal';

import ContextStore from 'utils/createContext';
import { push } from 'reduxModules/navigation/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';
import { postLiveLessonRating } from 'reduxModules/live/action';

import useLiveMainCheckoutCollectionUrl from 'routes/live/hooks/useLiveMainCheckoutCollectionUrl';
import getDateTimeWithStatus from 'routes/live/utils/getDateTimeWithStatus';

import LockPlayer from '../LockPlayer';
import UpcomingPlayer from '../UpcomingPlayer';
import WrongPlayer from '../WrongPlayer';
import ReplayAvailableSoonPlayer from '../ReplayAvailableSoonPlayer';
import ExternalPlayer from '../ExternalPlayer';

import {
  LOCKED,
  UPCOMING,
  VIDEO_REPLAY_SOON,
  VIDEO_NOT_UPLOADED,
  EXTERNAL_SOURCE,
  WRONG_REAL_LIVE,
} from 'routes/live/constants/liveEpisodeState';
import {
  REGULAR_CLASS_RATING_EPISODE_CLICK,
  REGULAR_CLASS_RATING_SUBMIT_CLICK,
} from 'constants/gtmEvent';

import lessonComingSoonImage from './images/lesson_coming_soon.svg';
import noUploadImage from './images/no-upload.svg';

import styles from './EpisodePlayer.scss';

const EpisodePlayer = ({
  className,
  topicCoverImageUrl,
  topicId,
  topicName,
}) => {
  useStyles(styles);

  const { t } = useTranslation(['live', 'rating']);
  const dispatch = useDispatch();

  const [showRatingModal, setShowRatingModal] = useState(false);

  const singleTopicCheckoutCollectionUrl = useLiveMainCheckoutCollectionUrl();

  const {
    playerRef,
    currentLesson,
    playCurrentLesson,
    pauseCurrentLesson,
    showAutoPlayController,
    playNextLesson,
    onEndedLesson,
    onVolumechange,
    updateLessonProgress,
    nextLesson,
    isMuted,
    onProgressDragStart,
    onProgressDragEnd,
    onForwardStart,
    onRewindStart,
  } = useContext(ContextStore);

  const {
    chatRoomType,
    chatRoomUrl,
    id,
    instructorIdList,
    instructorNameList,
    isRateable,
    isRated,
    name,
    state,
    time,
    url,
    videoId,
    videoIsReady,
    videoSource,
  } = currentLesson || {};

  const startAtStatusString = getDateTimeWithStatus(time, t);
  const startAt = `${time?.startDate}, ${time?.timeRange}`;

  const showRatingButton = isRateable && !isRated;

  const renderLessonPlayer = () => {
    switch (state) {
      case LOCKED:
        return (
          <LockPlayer
            title={t('live:topic.videoPlayerStatus.unlock.title')}
            description={t('live:topic.videoPlayerStatus.unlock.description')}
            buttonText={t('live:topic.videoPlayerStatus.unlock.cta')}
            onClick={() => dispatch(push(singleTopicCheckoutCollectionUrl))}
          />
        );
      case UPCOMING:
        return (
          <UpcomingPlayer
            image={lessonComingSoonImage}
            title={t('live:topic.videoPlayerStatus.comingSoon.title')}
            description={t(
              'live:topic.videoPlayerStatus.comingSoon.description',
              {
                date: startAt,
              },
            )}
            showTime={time}
          />
        );
      case VIDEO_REPLAY_SOON:
        return (
          <ReplayAvailableSoonPlayer
            title={name}
            description={t(
              'live:topic.videoPlayerStatus.replayAvailableSoonHint',
            )}
          />
        );
      case VIDEO_NOT_UPLOADED:
        return (
          <WrongPlayer
            image={noUploadImage}
            title={t('live:topic.videoPlayerStatus.noUploadedVideo.title')}
            description={t(
              'live:topic.videoPlayerStatus.noUploadedVideo.description',
            )}
          />
        );
      case WRONG_REAL_LIVE:
        return (
          <WrongPlayer
            image={noUploadImage}
            title={t('live:topic.videoPlayerStatus.noUploadedVideo.title')}
            description={t(
              'live:topic.videoPlayerStatus.noUploadedVideo.description',
            )}
          />
        );
      case EXTERNAL_SOURCE:
        return (
          <ExternalPlayer
            title={name}
            description={startAtStatusString}
            url={url}
            videoSource={videoSource}
            chatRoomType={chatRoomType}
            chatRoomUrl={chatRoomUrl}
            t={t}
          />
        );
      default: {
        if (!videoIsReady) return null;

        return (
          <BrightcovePlayer
            className={styles.inner}
            ref={playerRef}
            videoId={videoId}
            onPlay={playCurrentLesson}
            onPause={pauseCurrentLesson}
            onEnd={onEndedLesson}
            onSeek={updateLessonProgress}
            onLeave={updateLessonProgress}
            onVolumechange={onVolumechange}
            showAutoPlayController={showAutoPlayController}
            nextVideoTitle={nextLesson?.name}
            playNextVideo={playNextLesson}
            muted={isMuted}
            onProgressDragStart={onProgressDragStart}
            onProgressDragEnd={onProgressDragEnd}
            onForward={onForwardStart}
            onBackward={onRewindStart}
          />
        );
      }
    }
  };

  const onClickRating = () => {
    dispatch(
      sendGTMEvent({
        eventName: REGULAR_CLASS_RATING_EPISODE_CLICK,
        data: {
          topicId,
          topicName,
          episodeId: id,
          episodeName: name,
          instructorId: instructorIdList,
          instructorName: instructorNameList,
          section: 'live_topic',
        },
      }),
    );
    setShowRatingModal(true);
  };

  const onSubmitRating = useCallback(
    ({ stars, tagsString, comment }) => {
      dispatch(
        postLiveLessonRating({
          lessonId: id,
          stars,
          comment: `${tagsString}\n${comment}`,
        }),
      );
      dispatch(
        sendGTMEvent({
          eventName: REGULAR_CLASS_RATING_SUBMIT_CLICK,
          data: {
            topicId,
            topicName,
            instructorId: instructorIdList,
            instructorName: instructorNameList,
          },
        }),
      );
    },
    [dispatch, id, instructorIdList, instructorNameList, topicId, topicName],
  );

  return (
    <div className={cx(className)}>
      <div
        className={styles.wrapper}
        style={{ backgroundImage: `url(${topicCoverImageUrl})` }}
      >
        {renderLessonPlayer()}
      </div>
      {showRatingButton && (
        <RatingAction
          title={t('rating:regularClass.overviewRatingBanner.title')}
          actionText={t('rating:regularClass.overviewRatingBanner.cta')}
          onClickRating={onClickRating}
        />
      )}
      {currentLesson && (
        <RatingModal
          title={name}
          subTitle={startAtStatusString}
          onSubmit={onSubmitRating}
          onClose={() => setShowRatingModal(false)}
          visible={showRatingModal}
        />
      )}
    </div>
  );
};

EpisodePlayer.propTypes = {
  topicId: number.isRequired,
  topicName: string.isRequired,
  className: string,
  topicCoverImageUrl: string,
};

EpisodePlayer.defaultProps = {
  className: undefined,
  topicCoverImageUrl: '',
};

export default EpisodePlayer;
